import { ChatMessage, ConversationConfig, LLMProvider } from '../types';
import { MAX_HISTORY_MESSAGES } from '../constants';

/**
 * Manages conversation state, message history, and LLM communication for the chatbot widget.
 * The backend API handles all knowledge base and system prompt logic.
 */
export class ConversationManager {
  private readonly provider: LLMProvider;
  private messages: ChatMessage[] = [];
  private abortController: AbortController | null = null;

  constructor(config: ConversationConfig) {
    this.provider = config.provider;
  }

  /**
   * Creates a user message and appends it to the conversation history.
   * @param content - The text content of the user's message
   * @returns The created ChatMessage
   */
  addUserMessage(content: string): ChatMessage {
    const message: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    };
    this.messages.push(message);
    return message;
  }

  /**
   * Builds the messages array and sends it to the LLM provider.
   * Sends the conversation history directly since the backend API
   * handles system prompts and knowledge base management.
   * @returns The assistant's response as a ChatMessage
   */
  async sendToProvider(): Promise<ChatMessage> {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    const historySlice = this.messages.slice(-MAX_HISTORY_MESSAGES);

    const messages: Array<{ role: string; content: string }> = historySlice.map((msg) => ({
      role: msg.role,
      content: msg.content,
    }));

    const responseContent = await this.provider.complete(messages, signal);

    this.abortController = null;

    const assistantMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: responseContent,
      timestamp: new Date().toISOString(),
    };
    this.messages.push(assistantMessage);

    return assistantMessage;
  }

  /**
   * Returns a readonly view of the conversation history.
   */
  getHistory(): ReadonlyArray<ChatMessage> {
    return this.messages;
  }

  /**
   * Clears all messages and aborts any in-flight request.
   */
  clear(): void {
    this.abort();
    this.messages = [];
  }

  /**
   * Aborts the current in-flight request if one exists.
   */
  abort(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }
}
